import axios from '../axios-config';
import { UserClaim, ClaimType } from '../types/user-management';

export class UserManagementService {
  // Get available claim types
  static async getAvailableClaimTypes(): Promise<ClaimType[]> {
    const response = await axios.get('/api/Account/Claims/Available');
    return response.data;
  }

  // Get available roles
  static async getAvailableRoles(): Promise<any[]> {
    const response = await axios.get('/api/Account/Roles/Available');
    return response.data;
  }

  // Update user role
  static async updateUserRole(userId: string, userData: {
    id: string;
    altId?: string;
    name: string;
    email: string;
    birthDate?: string;
    role: string;
    grade?: number;
  }): Promise<void> {
    // Keep original endpoint (lowercase) for backward compat; prefer capitalized route if backend is case-sensitive
    await axios.put(`/api/Account/${userId}`, userData, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // User claim management
  static async addClaimToUser(userId: string, claim: UserClaim): Promise<void> {
    // Prefer /api/Account/Users/{id}/Claims (Swagger) but keep lowercase variant if server tolerant
    await axios.post(`/api/Account/Users/${userId}/Claims`, claim, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  static async removeClaimFromUser(userId: string, claim: UserClaim): Promise<void> {
  // Swagger shows DELETE /api/Account/Users/{id}/Claims expecting query params: type, value
  const url = `/api/Account/Users/${userId}/Claims?type=${encodeURIComponent(claim.type)}&value=${encodeURIComponent(claim.value)}`;
  await axios.delete(url);
  }

  // Fetch claims for a specific user (live, not from current logged-in profile)
  static async getUserClaims(userId: string): Promise<UserClaim[]> {
    // Expected backend endpoint (adjust if Swagger shows different): GET /api/Account/Users/{id}/Claims
    // Swagger (current) does NOT list a GET for /api/Account/Users/{id}/Claims (only POST/DELETE) nor GET /api/Account/Claims/User/{id}.
    // Some deployments may still expose one of these. We'll attempt several possibilities in order.
    const candidatePaths = [
      `/api/Account/Claims?id=${userId}`      // Fallback query param variant (if implemented server-side)
    ];

    let lastError: any = null;
    for (const path of candidatePaths) {
      try {
        const resp = await axios.get(path);
        const raw = resp.data || [];
        return raw.map((c: any) => ({
          type: c.type || c.claimType || c.ClaimType,
          value: String(c.value ?? c.claimValue ?? c.ClaimValue ?? '')
        }));
      } catch (err: any) {
        lastError = err;
        // Try next path
      }
    }
    console.warn('[UserManagementService] Failed to fetch user claims via all candidate endpoints. Returning empty list.', lastError);
    return [];
  }

  // Role management
  static async createRole(role: { name: string; abbreviation: string }): Promise<void> {
    await axios.post('/api/Account/Roles', role, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  static async deleteRole(roleName: string): Promise<void> {
    await axios.delete(`/api/Account/Roles/${encodeURIComponent(roleName)}`);
  }

  // Role claim management
  static async addClaimToRole(role: { id?: string; name: string } | string, claim: UserClaim): Promise<void> {
    const roleObj = typeof role === 'string' ? { name: role } : role;
    // Use id-based endpoint if we have a GUID id; fallback to roleName variant.
    const path = roleObj.id
      ? `/api/Account/Roles/${roleObj.id}/Claims`
      : `/api/Account/Roles/${encodeURIComponent(roleObj.name)}/Claims`;
    await axios.post(path, claim, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  static async removeClaimFromRole(role: { id?: string; name: string } | string, claim: UserClaim): Promise<void> {
    const roleObj = typeof role === 'string' ? { name: role } : role;
    const path = roleObj.id
      ? `/api/Account/Roles/${roleObj.id}/Claims`
      : `/api/Account/Roles/${encodeURIComponent(roleObj.name)}/Claims`;
  // DELETE role claims also expects optional query params (type, value) instead of body
  const url = `${path}?type=${encodeURIComponent(claim.type)}&value=${encodeURIComponent(claim.value)}`;
  await axios.delete(url);
  }

  // Fetch claims for a specific role by role id or name (assuming id)
  static async getRoleClaims(roleId: string): Promise<UserClaim[]> {
    // Backend signature: GET /api/Account/Roles/Claims/{id}
    const response = await axios.get(`/api/Account/Roles/Claims/${roleId}`);
    // Map backend shape { id, roleId, claimType, claimValue } -> UserClaim { type, value }
    return (response.data || []).map((c: any) => ({
      type: c.claimType || c.ClaimType,
      value: String(c.claimValue ?? c.ClaimValue ?? '')
    })) as UserClaim[];
  }
}
